import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gift, Linkedin, Link2, Check, Sparkles, Users, Trophy, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralHub() {
  const [email, setEmail] = useState(() => localStorage.getItem("fb_sub_email") || "");
  const [inputEmail, setInputEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = async (lookupEmail) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referrals/status`, { params: { email: lookupEmail } });
      setStatus(res.data);
      setEmail(lookupEmail);
      localStorage.setItem("fb_sub_email", lookupEmail.toLowerCase());
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not look up your referral status.");
      if (err.response?.status === 404) setEmail("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) fetchStatus(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const referralLink = status ? `${window.location.origin}/?ref=${status.referral_code}` : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const nextM = status?.next_milestone;
  const prevTarget = nextM
    ? [0, ...status.milestones.map((m) => m.target)].filter((t) => t < nextM.target).pop()
    : 0;
  const progressPct = nextM
    ? Math.min(100, Math.round(((status.referral_count - prevTarget) / (nextM.target - prevTarget)) * 100))
    : 100;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-28 pb-16 px-4 sm:px-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/10 mb-5"
          >
            <Gift size={26} className="text-brand-accent" />
          </motion.div>
          <p className="overline mb-3">Refer & Earn</p>
          <h1 className="font-cormorant text-4xl sm:text-5xl font-semibold text-foreground mb-4">
            Share the words, earn premium
          </h1>
          <p className="font-manrope text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Invite friends and colleagues to Freelanceblogger. When they subscribe through your
            link, you unlock free premium access — no payment needed.
          </p>
        </div>

        {/* Milestones overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { target: 3, reward: "1 month premium", icon: Sparkles },
            { target: 10, reward: "3 months premium", icon: Users },
            { target: 25, reward: "1 year premium", icon: Trophy },
          ].map(({ target, reward, icon: Icon }, i) => {
            const achieved = status && status.referral_count >= target;
            return (
              <motion.div
                key={target}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                data-testid={`milestone-card-${target}`}
                className={`rounded-xl border p-5 text-center ${
                  achieved ? "border-brand-accent bg-accent/5" : "border-border bg-card"
                }`}
              >
                <Icon size={20} className={`mx-auto mb-3 ${achieved ? "text-brand-accent" : "text-muted-foreground"}`} />
                <p className="font-cormorant text-2xl font-semibold text-foreground">{target} referrals</p>
                <p className="font-manrope text-xs text-muted-foreground mt-1">{reward}</p>
                {achieved && (
                  <span className="inline-flex items-center gap-1 mt-2 font-manrope text-xs font-semibold text-brand-accent">
                    <Check size={12} /> Earned
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Status or lookup */}
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : status ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="referral-status">
            {/* Premium banner */}
            {status.premium && (
              <div className="mb-6 rounded-xl border border-brand-accent bg-accent/5 p-4 flex items-center gap-3" data-testid="premium-active-banner">
                <Sparkles size={18} className="text-brand-accent shrink-0" />
                <p className="font-manrope text-sm text-foreground">
                  <span className="font-semibold">Premium unlocked!</span>{" "}
                  {status.premium_until && (
                    <>You have premium access until{" "}
                    {new Date(status.premium_until).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.</>
                  )}
                </p>
              </div>
            )}

            {/* Referral link card */}
            <div className="bg-card border border-border rounded-xl p-6 mb-6">
              <p className="overline mb-3">Your referral link</p>
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <code
                  data-testid="referral-link"
                  className="flex-1 px-4 py-3 rounded-lg bg-muted font-manrope text-sm text-foreground break-all"
                >
                  {referralLink}
                </code>
                <button
                  onClick={handleCopy}
                  data-testid="copy-referral-link-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors"
                >
                  {copied ? <Check size={15} /> : <Link2 size={15} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="referral-share-linkedin-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border font-manrope text-xs font-medium text-muted-foreground hover:text-foreground hover:border-brand-accent transition-colors"
                >
                  <Linkedin size={14} className="text-brand-accent" />
                  Share on LinkedIn
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent("I read Freelanceblogger by Elizabeth Neal — practical writing and content tips. Join me:")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="referral-share-twitter-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border font-manrope text-xs font-medium text-muted-foreground hover:text-foreground hover:border-brand-accent transition-colors"
                >
                  Share on X
                </a>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="overline">Your progress</p>
                <p className="font-cormorant text-3xl font-semibold text-brand-accent" data-testid="referral-count">
                  {status.referral_count}{" "}
                  <span className="font-manrope text-xs text-muted-foreground ml-1.5">
                    referral{status.referral_count === 1 ? "" : "s"}
                  </span>
                </p>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden mb-2">
                <div
                  className="h-full rounded-full bg-brand-accent transition-all duration-700"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="font-manrope text-xs text-muted-foreground" data-testid="next-milestone-text">
                {nextM
                  ? `${nextM.remaining} more referral${nextM.remaining === 1 ? "" : "s"} to earn ${nextM.label}`
                  : "You've earned every milestone — thank you for spreading the word!"}
              </p>
            </div>

            <p className="font-manrope text-xs text-muted-foreground text-center mt-6">
              Checking for <span className="font-medium">{email}</span> ·{" "}
              <button
                onClick={() => { setStatus(null); setEmail(""); }}
                className="underline hover:text-foreground"
                data-testid="change-email-btn"
              >
                use a different email
              </button>
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-6 max-w-md mx-auto text-center"
          >
            <Mail size={22} className="text-brand-accent mx-auto mb-3" />
            <h2 className="font-cormorant text-2xl font-semibold text-foreground mb-2">Get your referral link</h2>
            <p className="font-manrope text-xs text-muted-foreground mb-5">
              Enter your subscribed email to see your unique link and progress.
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); if (inputEmail) fetchStatus(inputEmail); }}
              className="flex flex-col sm:flex-row gap-2"
            >
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                data-testid="referral-email-input"
                className="flex-1 px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
              <button
                type="submit"
                data-testid="referral-lookup-btn"
                className="px-5 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors"
              >
                Get my link
              </button>
            </form>
            <p className="font-manrope text-xs text-muted-foreground mt-4">
              Not subscribed yet?{" "}
              <Link to="/subscribe" className="text-brand-accent hover:underline" data-testid="referral-subscribe-link">
                Subscribe free
              </Link>{" "}
              to get your link.
            </p>
          </motion.div>
        )}
      </main>

      <Footer />
    </div>
  );
}
